/**
 * Bank account class component
 */
import axios from 'axios'

class BankAccount {
    /**
     * BankAccount class component constructor
     * @param {HTMLElement} domElement
     */
    constructor(domElement = null) {
        this.container = $(domElement)
        if(!this.container.length) {
            console.error('Please provide proper DOM element for Complaint/BankAccount class')
            return
        }

        this.form = this.container.closest('form')

        this.nextButton = this.container.parent().find('button.next')
        this.countryHolderSelect = this.container.find('.country-holder-select')
        this.countryInfoSelect = this.container.find('select.country-info-select')

        this.bankValues = []
        this.bankPrefixes = {}

        this.init()
        this.setEventListeners()
    }

    /**
     * Destructor method
     */
    destroy() {
        this.removeEventListeners()
    }

    setEventListeners() {
        this.countryHolderSelect.on('change', this.handleCountryHolderChange.bind(this))
        this.container.on('change', '.name-of-bank-input-serbia', this.setBankPrefix.bind(this))

        this.container.on('change', '.swift-input', this.handleSwiftChange.bind(this))
    }

    /**
     * Remove attached event listeners
     */
    removeEventListeners() {
        this.container.off('click')
        this.container.off('change')
    }

    init() {
        const { countriesData, banksApiRoute } = this.container.data()
        this.countriesData = countriesData
        this.banksApiRoute = banksApiRoute

        this.countryHolderValue = this.countryHolderSelect.val()

        const selectizeInstance = this.countryInfoSelect[0].selectize
        if(selectizeInstance) {
            selectizeInstance.lock()
            selectizeInstance.setValue(this.countryHolderValue)
        }

        this.setFieldsByCountryHolder()
        this.setAccountHolderFieldValue()
        this.setSwiftBankNameField()

        this.setSelectSizings()
    }

    handleCountryHolderChange(event) {
        this.countryHolderValue = this.countryHolderSelect.val()

        const selectizeInstance = this.countryInfoSelect[0].selectize
        if(selectizeInstance) {
            selectizeInstance.setValue(this.countryHolderValue)
        }

        this.setFieldsByCountryHolder()
    }

    setFieldsByCountryHolder() {
        if(!this.countryHolderValue) {
            this.nextButton.prop('disabled', true)
            this.container.find('.bank-field-container').addClass('d-none')
            this.container.find('.top-text-label').removeClass('d-none')

            this.bankValues = []
            this.bankPrefixes = {}

            return
        }

        let selectedField
        Object.keys(this.countriesData).forEach(key => {
            const values = this.countriesData[key]

            if(values.includes(this.countryHolderValue)) {
                selectedField = key
            }
        })

        selectedField = selectedField || 'notListed'
        this.container.find('.bank-field-container').each((_, item) => {
            const $field = $(item)
            const { list } = $field.data()

            if (list.includes(selectedField)) {
                $field.removeClass('d-none')
            } else {
                $field.addClass('d-none')
            }
        })

        if(selectedField === 'notAvailable') {
            this.nextButton.prop('disabled', true)
            this.container.find('.top-text-label').addClass('d-none')
        } else {
            this.nextButton.prop('disabled', false)
            this.container.find('.top-text-label').removeClass('d-none')
        }

        this.container.find('.bank-fields-container').attr('data-country', selectedField)

        this.setBankFields()
        this.setBankPrefix()
        this.setSwiftBankNameField()
    }

    /**
     * SWIFT input change
     * @param {Object} event
     */
    async handleSwiftChange(event) {
        const $input = $(event.currentTarget)
        const $bankInput = this.container.find('.name-of-bank-input')

        const swiftValue = $input.val()
        if (!swiftValue && !$bankInput.val()) {
            $bankInput.prop('disabled', true)

            return
        }

        this.fetchBankBySwift(swiftValue)
    }

    /**
     * Fetch and set bank name by SWIFT
     * @param {String} swiftValue
     */
    async fetchBankBySwift(swiftValue = '') {
        const $bankInput = this.container.find('.name-of-bank-input')

        try {
            // Make request
            const response = await axios.get(`/sap/find-bank-name?swift=${swiftValue}`)

            const { data: {
                bankName
            } } = response

            if (bankName) {
                $bankInput.val(bankName)
            }

            $bankInput.prop('disabled', false)
        } catch(err) {
            console.error(err)
            $bankInput.prop('disabled', false) // If we have error with fetzching, enable user free input

            throw err
        }
    }

    /**
     * Set options to bank field(s)
     */
    async setBankFields() {
        let $input = this.container.find('.name-of-bank-input-serbia')
        if(this.countryHolderValue === 'RU') {
            $input = this.container.find('.russian-name-of-bank-input')
        }

        const selectizeInstance = $input[0].selectize

        try {
            if (selectizeInstance) {
                selectizeInstance.lock()
            }

            await this.fetchBankOptions()
            this.setSelectizeOptions(selectizeInstance, this.bankValues)

            if (selectizeInstance) {
                selectizeInstance.unlock()
            }

        } catch(err) {
            if (selectizeInstance) {
                selectizeInstance.unlock()
            }

            throw err
        }
    }

    /**
     * Fetch options for bank dropdown
     */
    async fetchBankOptions() {
        try {
            const response = await axios.get(`${this.banksApiRoute}?country=${this.countryHolderValue}`)
            const { data } = response

            // Options
            this.bankValues = data.map(item => ({
                text: item.name,
                value: item.name,
            }))

            // Store for bank prefixes
            this.bankPrefixes = data.reduce((acc, item) => {
                return {
                    ...acc,
                    [item.name]: item.account_number_prefix
                }
            }, {})

            return

        } catch(err) {
            console.error(err)

            this.bankValues = []
            this.bankPrefixes = {}

            throw err
        }
    }

    /**
     * Set prefix value to Account number input [*RS only*]
     */
    setBankPrefix() {
        if (this.countryHolderValue !== 'RS') {
            return
        }

        const $input = this.container.find('.name-of-bank-input-serbia')
        const bankNameValue = $input.val()

        const $accountNumberInput = this.container.find('.serbia-account-number')

        const value = $accountNumberInput.val()
        const allPrefixes = Object.values(this.bankPrefixes)

        if (!value || allPrefixes.includes(value)) {
            const prefixValue = this.bankPrefixes[bankNameValue]

            if (prefixValue) {
                $accountNumberInput.val(prefixValue)
                $accountNumberInput.trigger('focus')
            }
        }
    }

    /**
     * Set disabled/enabled prop by value
     */
    setSwiftBankNameField() {
        const $swiftInput = this.container.find('.swift-input:visible:first')
        const $input = this.container.find('.name-of-bank-input')

        if ($input.val()) {
            $input.prop('disabled', false)

            return
        }

        const swiftValue = $swiftInput.val()

        if (swiftValue) {
            this.fetchBankBySwift(swiftValue)
        } else {
            $input.prop('disabled', true)
        }
    }

    /**
     * Set value to Account holder by "Authorized person value"
     */
    setAccountHolderFieldValue() {
        const $accountHolderFieldContainer = this.container.find('.bank-field-container[data-name="account_information_name_and_surname"]')
        const $field = $accountHolderFieldContainer.find('.form-control')
        const $label = $accountHolderFieldContainer.find('.form-label')

        const { value, label } = $accountHolderFieldContainer.data()

        if(value) {
            $field.val(value)
        }

        if(label) {
            $label.text(label)
            $field.attr('placeholder', label)
        }
    }

    /**
     * Auto-sizing selectize wrapper after initialization
     * jquery .height() won't work on hidden parent
     */
    setSelectSizings() {
        const selects = this.container.find('.form-input[data-input-type="select"]')
        selects.each((_, item) => {
            const selectizeInstance = item.selectize
            if (!selectizeInstance) {
                return
            }

            const $control = selectizeInstance.$control
            const inputLabel = selectizeInstance.$wrapper.next('.form-label')
            const trimmedValue = selectizeInstance.getValue().trim()

            // Set field height
            const labelHeight = inputLabel.height()
            if (labelHeight > 20 && trimmedValue) {
                $control.css({
                    height: 'auto',
                    paddingTop: `${labelHeight + 10}px`
                })
            } else {
                $control.css({
                    height: '',
                    paddingTop: ''
                })
            }
        })
    }

    /**
     * Set options for input
     * @param {selectizeInstance} $input
     * @param {Array} options
     */
    setSelectizeOptions(inputSelectizeInstance, options = []) {
        if(inputSelectizeInstance) {
            const currentValue = inputSelectizeInstance.getValue()
            //const optionsValues = options.map(item => item.value)
            //const isCurrentValueInNewOptions = optionsValues.includes(currentValue)

            // Clear options
            inputSelectizeInstance.clearOptions(true)
            inputSelectizeInstance.clearOptionGroups(true)

            // Add new options
            inputSelectizeInstance.addOption(options)
            inputSelectizeInstance.refreshOptions(false)

            // Keep selection of "old value" if it is in new options
            if (currentValue) {
                inputSelectizeInstance.addItem(currentValue, true)
            } else {
                inputSelectizeInstance.clear()
            }
        }
    }
}

export default BankAccount
